import React from 'react';
import { Navigate } from 'react-router-dom';
import RestrictionBlock, { RestrictionInfo } from '../components/panel/RestrictionBlock';
import SteamLinkRedirect from '../components/auth/SteamLinkRedirect';
import FullScreenLoader from '../components/loader/FullScreenLoader';
import Modal from '../components/modal/Modal';
import modalCss from '../components/modal/modal.module.css';
import { ErrorResponse } from 'oidc-client-ts';
import { userManager, renewTokenSilent, startLogin, startLogout, hasStoredSession, ensureFreshToken } from '../utils/oidc';
import { trackNow } from '../utils/analytics';
import { USER_TOKEN_KEY, USER_REFRESH_TOKEN_KEY, decodeTokenRoles, AUTH_EXPIRED_EVENT } from '../utils/authTokens';

const AUTH_BASE = process.env.REACT_APP_AUTH_URL      ?? 'http://localhost:3002';

export { USER_TOKEN_KEY, USER_REFRESH_TOKEN_KEY };

// Профиль последнего входа: при возврате во вкладку (в т.ч. после выгрузки
// вкладки браузером) и при переходе с сайта панель открывается сразу, а
// /auth/me обновляет данные в фоне. sessionStorage — живёт только в этой
// вкладке и не переживает закрытие браузера.
const PROFILE_CACHE_KEY = 'panel_profile_cache';

function readCachedProfile(): TUserInfo | null {
  if (!hasStoredSession()) return null;
  try {
    const raw = window.sessionStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) as TUserInfo : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: TUserInfo | null): void {
  try {
    if (profile) window.sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    else         window.sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* приватный режим — просто без кэша */ }
}

export interface TLegalDocumentShort {
  id:           number;
  slug:         string;
  title:        string;
  version:      string;
  body?:        string;
  effective_at: string;
}

export interface TUserInfo {
  id?:                 number;
  login?:              string;
  display_name?:       string;
  avatar_url?:         string | null;
  email?:              string;
  status?:             string;
  created_at?:         string;
  /** Дата рождения YYYY-MM-DD, необязательная. */
  birth_date?:         string | null;
  /** Согласие на рекламные рассылки — снимается самим пользователем в профиле. */
  marketing_consent?:  boolean;
  is_admin?:           boolean;
  pending_consents?:   TLegalDocumentShort[];
  service_restriction?: RestrictionInfo | null;
  steam?: {
    steam_id:     string;
    steam_name:   string | null;
    steam_avatar: string | null;
  } | null;
  /** Включена ли 2FA (TOTP) на аккаунте. */
  totp_enabled?:       boolean;
  /** 2FA обязательна (есть админ-роли): без неё роли в токен не попадают. */
  mfa_required?:       boolean;
  /** Пройден ли второй фактор в текущей сессии. */
  mfa?:                boolean;
  steam_linked?:       boolean;
  /** Привязанный аккаунт форума: на форум входят через ЛК */
  forum?: {
    forum_user_id: number;
    username:      string;
    linked_at:     string;
    profile_url:   string;
    /** Снимок профиля форума (выгружается раз в 5 минут); старый ЛК их не присылает */
    avatar_url?:      string | null;
    avatar_edit_url?: string;
    user_title?:      string | null;
    message_count?:   number | null;
    reaction_score?:  number | null;
    register_date?:   string | null;
    groups?:          string[];
  } | null;
}

interface TUserAuthContext {
  user:    TUserInfo | null;
  loading: boolean;
  /** Старт OIDC-логина (редирект на портал amazing_auth). Аргументы игнорируются — оставлены для обратной совместимости. */
  login:   (identifier?: string, password?: string) => Promise<void>;
  logout:  () => void;
  refresh: () => Promise<void>;
}

const UserAuthContext = React.createContext<TUserAuthContext | null>(null);

/** Профиль; `undefined` — сервер недоступен (сеть), `null` — входа нет. */
async function fetchProfile(token: string): Promise<TUserInfo | null | undefined> {
  try {
    const res = await fetch(`${AUTH_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) return undefined;
    const data = await res.json() as TUserInfo;

    // Доступ к админ-панели определяем по claim `roles` в OIDC-токене:
    // непустой набор ролей = администратор (как в backend authMiddleware).
    data.is_admin = decodeTokenRoles(token).length > 0;
    return data;
  } catch {
    return undefined;
  }
}

export const UserAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cached] = React.useState(readCachedProfile);
  const [user,    setUser]    = React.useState<TUserInfo | null>(cached);
  const [loading, setLoading] = React.useState(!cached);
  const [sessionExpired, setSessionExpired] = React.useState(false);

  /**
   * Проверка входа и загрузка профиля. `silent` — фоновое обновление поверх
   * уже показанного профиля: без загрузчика, и сетевой сбой не выбивает из панели.
   */
  const doRefresh = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let oidcUser = await userManager.getUser();

      // Истёкший токен — пробуем тихое обновление (silent renew).
      // renewTokenSilent — single-flight: параллельный refresh из adminApi
      // не запустит второй грант (важно при rotateRefreshToken на провайдере).
      if (oidcUser && oidcUser.expired) {
        try { oidcUser = await renewTokenSilent(); }
        catch (e) {
          // Сеть пропала посреди фонового обновления — остаёмся с тем, что
          // показано; отказ провайдера (ErrorResponse) значит, что входа больше нет
          if (silent && !(e instanceof ErrorResponse)) return;
          oidcUser = null;
        }
      }

      const token = oidcUser?.access_token ?? null;
      if (!token) {
        // Нет валидного токена — чистим localStorage, чтобы страницы не слали протухший
        localStorage.removeItem(USER_TOKEN_KEY);
        localStorage.removeItem(USER_REFRESH_TOKEN_KEY);
        writeCachedProfile(null);
        setUser(null); setLoading(false); return;
      }

      // Синхронизируем валидный (при необходимости обновлённый) токен в localStorage:
      // его читают напрямую клиенты страниц (тикеты, профиль), не делающие refresh сами.
      localStorage.setItem(USER_TOKEN_KEY, token);
      if (oidcUser?.refresh_token) localStorage.setItem(USER_REFRESH_TOKEN_KEY, oidcUser.refresh_token);

      const profile = await fetchProfile(token);
      if (profile === undefined && silent) return;
      writeCachedProfile(profile ?? null);
      setUser(profile ?? null);
    } catch {
      if (!silent) setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Есть профиль с прошлого раза — показываем его сразу и сверяем в фоне
  React.useEffect(() => { void doRefresh(!!cached); }, [doRefresh, cached]);

  // Возврат во вкладку: браузер притормаживал таймер обновления токена, поэтому
  // обновляем сами, до того как страница начнёт слать запросы с протухшим.
  const hasUser = !!user;
  React.useEffect(() => {
    if (!hasUser) return undefined;
    const check = () => {
      if (document.hidden) return;
      ensureFreshToken()
        .then(u => { if (!u) setSessionExpired(true); })
        .catch(e => { if (e instanceof ErrorResponse) setSessionExpired(true); });
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', check);
    };
  }, [hasUser]);

  // Запрос API получил 401 и тихое обновление не помогло (adminApi)
  React.useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // OIDC: логин — редирект на портал авторизации (PKCE). Аргументы игнорируются.
  const login = async (_identifier?: string, _password?: string) => {
    await startLogin();
  };

  // RP-initiated logout — завершает сессию и на провайдере
  const logout = () => {
    writeCachedProfile(null);
    setUser(null);
    void startLogout();
  };

  const refresh = React.useCallback(() => doRefresh(true), [doRefresh]);

  return (
    <UserAuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
      {sessionExpired && (
        <Modal title="Сессия истекла" width={420} onClose={() => setSessionExpired(false)}>
          <p className={modalCss.text}>
            Войдите снова, чтобы продолжить. Если вы что-то писали на этой странице,
            скопируйте текст перед входом.
          </p>
          <div className={modalCss.actions}>
            <button
              type="button"
              className={`${modalCss.btn} ${modalCss.btnSecondary}`}
              onClick={() => setSessionExpired(false)}
            >
              Позже
            </button>
            <button
              type="button"
              className={`${modalCss.btn} ${modalCss.btnPrimary}`}
              onClick={() => {
                trackNow('auth_forced_logout', 'panel', { reason: 'session_expired', path: window.location.pathname });
                void startLogin(window.location.pathname + window.location.search);
              }}
            >
              Войти снова
            </button>
          </div>
        </Modal>
      )}
    </UserAuthContext.Provider>
  );
};

/**
 * Переход на портал входа. Тот же загрузчик, что и при проверке входа, —
 * без промежуточного экрана «Перенаправление…».
 */
export const LoginRedirect: React.FC<{ guard: string }> = ({ guard }) => {
  React.useEffect(() => {
    trackNow('auth_forced_logout', 'panel', { reason: 'guard_no_user', path: window.location.pathname, guard });
    void startLogin(window.location.pathname + window.location.search);
  }, [guard]);
  return <FullScreenLoader caption="Переход ко входу" />;
};

export function useUserAuth(): TUserAuthContext {
  const ctx = React.useContext(UserAuthContext);
  if (!ctx) throw new Error('useUserAuth must be used inside UserAuthProvider');
  return ctx;
}

export function UserAuthGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, loading, logout } = useUserAuth();
  const [dismissedRestriction, setDismissedRestriction] = React.useState(false);

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <LoginRedirect guard="user" />;
  }

  // Блокировка входа в режиме техработ выполняется на стороне amazing_auth
  // (SSR-форма пароля перед выдачей токена), поэтому здесь дополнительной
  // клиентской проверки больше нет.

  if (user.status === 'email_unverified') {
    return <Navigate to="/panel/register" replace />;
  }

  // Привязка Steam — необязательный шаг; показываем SSR-страницу портала.
  if (user.status === 'steam_unlinked') {
    return <SteamLinkRedirect />;
  }

  // Блокировка сервиса — показываем модалку, а после закрытия выполняем logout
  if (user.service_restriction && !dismissedRestriction) {
    return (
      <RestrictionBlock
        data={user.service_restriction}
        onClose={() => {
          setDismissedRestriction(true);
          logout();
        }}
      />
    );
  }

  // Если есть непринятые обязательные документы — перенаправляем на страницу согласий
  if (user.pending_consents && user.pending_consents.length > 0) {
    return <Navigate to="/panel/consent" replace />;
  }

  return <>{children}</>;
}
